import React, { useRef, useState, useEffect, Children } from 'react';
import PropTypes from 'prop-types';
import SwiperCore, { Navigation, Pagination, A11y, Autoplay, Lazy } from 'swiper';
import { Swiper } from 'swiper/react';
import classNames from 'classnames';

// Styles
import './Slider.css';
import './Slider.styl';

SwiperCore.use([Navigation, Pagination, A11y, Autoplay, Lazy]);

export default function Slider(props) {
  const {
    className,
    children,
    swiperParams,
    lazyLoad,
    isOutsideNavigation,
    isOutsidePagination,
    newest,
    onSwiper: onSwiperProp,
    onSlideChange,
  } = props;

  const swiperRef = useRef(null);
  // false - нет левой, true - нет правой, null - обе стрелки, undefined - вообще убрать стрелки
  const [isEnd, setIsEnd] = useState(false);

  // при изменении числа слайдов Swiper нужно пересчитать layout и подгрузить lazy-контент
  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper) {
      return;
    }

    if (swiper.params.loop && swiper.slides.length > 1) {
      swiper.loopDestroy();
      swiper.loopCreate();
    }

    swiper.update();

    if (swiper.lazy) {
      swiper.lazy.load();
    }
  }, [Children.count(children)]);

  // если в конце прокрутки сработал лейзи лоадинг и подтянулись новые элементы
  // то сделать обе стрелки доступными
  useEffect(() => {
    setIsEnd(prev => (prev === true ? null : prev));
  }, [Children.count(children)]);

  const goNext = () => {
    if (swiperRef.current !== null) {
      swiperRef.current.slideNext();
    }
  };

  const goPrev = () => {
    if (swiperRef.current !== null) {
      swiperRef.current.slidePrev();
    }
  };

  const onSwiper = swiper => {
    swiperRef.current = swiper;

    if (swiper.lazy) {
      swiper.lazy.load();
    }

    if (swiper.isBeginning && swiper.isEnd) {
      setIsEnd(undefined);
    }

    onSwiperProp?.(swiper);
  };

  const onReachBeginning = () => {
    setIsEnd(false);
  };

  const onFromEdge = () => {
    setIsEnd(null);
  };

  const onReachEnd = () => {
    setIsEnd(true);
    // подтянуть новые элементы в конце прокрутки
    lazyLoad?.();
  };

  if (isOutsidePagination) {
    swiperParams.pagination = {
      ...(swiperParams.pagination || {}),
      el: '.swiper-pagination-outside',
    };
  }

  return (
    <div className={classNames('swiper', className, { swiper_newest: newest })}>
      <Swiper
        {...swiperParams}
        onSwiper={onSwiper}
        onSlideChange={onSlideChange}
        onReachEnd={onReachEnd}
        onFromEdge={onFromEdge}
        onReachBeginning={onReachBeginning}
      >
        {children}
      </Swiper>
      {isOutsidePagination && <div className="swiper-pagination swiper-pagination-outside" />}
      {isOutsideNavigation && (
        <button
          className={classNames('swiper-button-prev', 'swiper-button-prev-outside', {
            'swiper-button-prev-outside_disabled': isEnd === undefined || isEnd === false,
          })}
          onClick={goPrev}
          type="button"
        />
      )}
      {isOutsideNavigation && (
        <button
          className={classNames('swiper-button-next', 'swiper-button-next-outside', {
            'swiper-button-next-outside_disabled': isEnd === undefined || isEnd === true,
          })}
          onClick={goNext}
          type="button"
        />
      )}
    </div>
  );
}

Slider.defaultProps = {
  className: '',
  swiperParams: {},
  lazyLoad: () => null,
  isOutsideNavigation: false,
  isOutsidePagination: false,
  newest: false,
  onSwiper: null,
  onSlideChange: null,
};

Slider.propTypes = {
  children: PropTypes.any.isRequired,
  className: PropTypes.string,
  swiperParams: PropTypes.object,
  lazyLoad: PropTypes.func,
  isOutsideNavigation: PropTypes.bool,
  isOutsidePagination: PropTypes.bool,
  newest: PropTypes.bool,
  onSwiper: PropTypes.func,
  onSlideChange: PropTypes.func,
};
